import type { HubspotList, HubspotAccount, HubspotContact } from '@types';
import { useSessionStore } from '@features/auth';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHubspotApi } from '@features/Settings/hooks/useHubspotApi.ts';
import { useHubspotSyncApi } from '@features/contacts/hooks/useHubspotSyncApi.ts';
import { IntegrationsContactTypes } from '@features/Settings/enums/IntegrationsEnums.ts';
import { type ContactInfoDto, type ContactListDto } from '@features/contacts';
import useWizardStore from '../../wizard/store/useWizardStore.ts';
import { handleError } from '@utils/handleAPIError.ts';
import { writeWarningLog } from '@lib/logger/logWarning.ts';
import { writeInfoLog } from '@lib/logger/logInfo.ts';
import { writeErrorLog } from '@lib/logger/logError.ts';

function convertHubspotListsToTendrilLists (
  hubspotLists: HubspotList[],
  organizationId: string
): ContactListDto[]  {
  return hubspotLists.map((hubspotList) => {
    const { listId, name } = hubspotList;
    return {
      id: listId,
      organizationId,
      title: name,
      tendrilId: listId,
      type: IntegrationsContactTypes.Hubspot
    };
  });
}

function convertHubspotContactsToTendrilContacts (hubspotContacts: HubspotContact[]): ContactInfoDto[] {
  return hubspotContacts.map(hubspotContact => {
    const { vid, properties, notes } = hubspotContact;
    const contactListId = hubspotContact['portal-id'] ?? '';
    const name = `${properties.firstname?.value ?? ''} ${properties.lastname?.value ?? ''}`;
    const phoneValue = properties.phone?.value ?? '';
    const mobileValue = properties.mobilephone?.value ?? '';
    const phone = phoneValue !== '' ? phoneValue : mobileValue;
    return {
      id: vid,
      contactListId,
      name,
      phone,
      jobTitle: properties.jobtitle?.value ?? '',
      notes: (notes != null) ? notes.join('\n').replace(/(<([^>]+)>)/gi, '') : '',
      accountName: properties.company?.value ?? '',
      email: properties.email?.value ?? '',
    };
  });
}

interface HubspotViewModelReturn {
  retrieveHubspotAccounts: ()=> Promise<string | HubspotAccount[]>,
  retrieveHubspotContactsLists: ()=> Promise<ContactListDto[] | undefined>,
  retrieveHubspotContacts: (contactsListId: string)=> Promise<ContactInfoDto[] | undefined>,
  nonce: string,
}

export function useHubspotViewModel (): HubspotViewModelReturn {
  const { setHubspotNonce, setHubspotPortalId } = useWizardStore();
  const currentUser = useSessionStore((sessionState) => sessionState.currentUser);
  const organizationId = String(currentUser?.organizationId ?? '').trim();
  const userId = String(currentUser?.userId ?? '').trim();
  const nonceRef = useRef<string>('');
  const [accountsAreLinked, setAccountsAreLinked] = useState(true);
  
  const { getAllHubspotAccounts } = useHubspotApi();
  const { getHubspotContactsLists, getHubspotContacts } = useHubspotSyncApi();


  async function retrieveHubspotAccounts (): Promise<string | HubspotAccount[]> {
    const { data: accountsResponse, error } = await getAllHubspotAccounts(userId, organizationId);

    if (accountsResponse?.length === 0) {
      await writeWarningLog(
        'No Hubspot linked accounts were found.',
        'useHubspotViewModel.retrieveHubspotAccounts',
        { accountsResponse }
      );
    }

    if (accountsResponse === undefined ) {
      void handleError( 
        'Error while getting hubspot accounts.',
        error ?? 'Unknown error', 
        retrieveHubspotAccounts.name
      );
      return error?.message ?? 'Error while getting hubspot accounts.';
    }

    setHubspotPortalId(accountsResponse[0].portalId ?? 0);
    nonceRef.current = accountsResponse[0]?.nonce;
    return accountsResponse;
    
  }
  
  const retrieveHubspotContactsLists = useCallback(async (): Promise<ContactListDto[] | undefined> => {
    if (!accountsAreLinked) {
      return;
    }
    
    const { data } = await getHubspotContactsLists(organizationId, userId);
  
    if (data === undefined || data.length === 0) {
      return undefined;
    }
    const convertedLists = convertHubspotListsToTendrilLists(data, organizationId);
    await writeInfoLog(
      'Hubspot lists retrieved successfully', 
      'useHubspotViewModel.retrieveHubspotContactsLists', 
      { count: convertedLists.length }
    );

    setHubspotNonce(nonceRef.current);

    return convertedLists;
    
  }, [getHubspotContactsLists, userId, organizationId]);
  
  const retrieveHubspotContacts = useCallback(async (contactsListId: string) => {
    const { data: accountsData } = await getAllHubspotAccounts(userId, organizationId);

    let HSnonce;
    if (accountsData !== undefined) {
      HSnonce = accountsData[0].nonce ?? '';
    }
    
    const { data, error } = await getHubspotContacts(String(HSnonce), contactsListId);

    if (data === undefined) {
      await writeErrorLog(error?.message ?? 'Error while retrieving contacts.',
        retrieveHubspotContacts.name,
        { nonce: nonceRef, orgId: organizationId }
      );
      return undefined;
    }

    const convertedContacts = convertHubspotContactsToTendrilContacts(data);
    await writeInfoLog(
      'Hubspot contacts retrieved successfully', 
      'useHubspotViewModel.retrieveHubspotContacts', 
      { count: convertedContacts.length }
    );

    return convertedContacts;

  }, [getHubspotContacts, nonceRef, organizationId]);
  
  useEffect(() => {
    setAccountsAreLinked(nonceRef.current === '');
  }, []);
  
  return {
    retrieveHubspotAccounts,
    retrieveHubspotContactsLists,
    retrieveHubspotContacts,
    nonce: nonceRef.current
  };
}